import React from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import { withStyles } from '@mui/styles';

import { Icon, Typography, Tooltip } from '@mui/material';
import { StoreUtil, SpecialtyUtil } from 'doctivity-shared/utils';
import { withRouter } from 'utils';
import { DynamicTable, ProvidersTabs } from 'components';
import { favoritesByType } from 'store/actions/favoritesActions';
import { queryTaxonomyCodes } from 'store/actions/taxonomyActions';

const styles = (theme) => ({
  title: {
    padding: theme.spacing(3),
    paddingBottom: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    backgroundColor: theme.palette.primary.accent,
  },
  headerTitle: {
    color: '#FFFFFF',
    fontSize: 24,
    paddingRight: 30,
  },
  container: {
    padding: theme.spacing(3),
  },
  affiliatedIcon: {
    fontSize: 14,
    verticalAlign: 'text-top',
    color: theme.palette.secondary.main,
  },
  header: {
    padding: theme.spacing(3),
    paddingBottom: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
  },

});

class FavoriteProvidersPage extends React.Component {
  constructor(props) {
    super(props);

    this.columns = [
      {
        label: '',
        key: 'affiliated',
        style: { width: 14 },
        sortable: false,
        showFilter: false,
        format: (affiliated) => {
          if (affiliated) {
            return (
              <Tooltip
                placement='bottom-start'
                title={
                  <Typography
                    style={{
                      fontSize: 13,
                      fontWeight: 200,
                    }}
                  >
                    Affiliated Provider
                  </Typography>
                }
              >
                <div
                  style={{
                    width: 14,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Icon className={props.classes.affiliatedIcon}>hub</Icon>
                </div>
              </Tooltip>
            );
          }
          return <></>;
        },
      },
      {
        label: 'Last Name',
        key: 'last_name',
        fulltext: true,
        style: { minWidth: 100 },
      },
      {
        label: 'First Name',
        key: 'first_name',
        fulltext: true,
        style: { minWidth: 100 },
      },
      {
        label: 'Credential',
        key: 'credential',
        style: { width: 105, minWidth: 105, maxWidth: 105 },
        format: (value) => (
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: 105 }} title={value}>
            {value}
          </div>
        ),
      },
      {
        label: 'NPI',
        key: 'npi',
        style: { width: 90, minWidth: 90, maxWidth: 90, verticalAlign: 'top' },
      },
      {
        label: 'Specialty',
        key: 'taxonomy.classification',
        filterFields: ['primary_taxonomy'],
        style: { minWidth: 200 },
        format: (classification, row) => SpecialtyUtil.getNameFromTaxonomy(row.taxonomy),
        specialtySearch: true,
      },
      {
        label: 'Organization',
        key: 'location.organization.name',
        fulltext: true,
        hideForMobile: true,
        style: { minWidth: 220 },
      },
      {
        label: 'City',
        key: 'location.city',
        fulltext: true,
        style: { minWidth: 120 },
        format: (value) => (
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={value}>
            {value}
          </div>
        ),
      },
      {
        label: 'State',
        key: 'location.state',
        style: { width: 50 },
        filterExact: true,
        autoCapitalize: true,
      },
    ];

    this.state = {
      query: {
        include: [
          {
            association: 'taxonomy',
          },
          {
            attributes: ['id', 'city', 'state'],
            association: 'location',
            include: [{
              association: 'organization',
              attributes: ['id', 'name'],
              required: false,
            }],
          },
        ],
        order: [['last_name', 'ASC']],
        limit: 25,
        offset: 0,
      },
    };
  }

  componentDidMount() {
    this.fetchData();

    const values = JSON.parse(sessionStorage.getItem('selectedSpecialties'));
    if (values && values.length > 0) {
      this.fetchSpecialties(values);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.clientId !== prevProps.clientId
      || this.props.userId !== prevProps.userId) {
      this.fetchData();
    }
  }

  fetchData() {
    const { dispatch, clientId, userId } = this.props;
    const { where, include, order, limit, offset } = this.state.query;

    dispatch(favoritesByType(
      clientId,
      userId,
      'Providers',
      {
        where,
        include,
        order,
        limit,
        offset,
      }
    ));
  }

  fetchSpecialties(values) {
    const { dispatch } = this.props;
    dispatch(queryTaxonomyCodes({
      where: { code: values },
      order: [['code', 'ASC']],
      limit: 10000,
      offset: 0,
    }));
  }

  render() {
    const { classes, taxonomyCodes } = this.props;

    const { query } = this.state;

    const favorites = this.props.favorites;

    // Build display data that passes count to DynamicTable for proper pagination,
    // and shows a loading spinner during re-fetches (e.g. when filters change).
    let favoritesData;
    if (StoreUtil.isLoading(favorites)) {
      favoritesData = { placeholder: true, showLoading: true, showNoData: false };
    } else if (favorites.data) {
      favoritesData = {
        count: favorites.data.count,
        rows: favorites.data.rows
          .filter((row) => row.provider)
          .map((row) => row.provider),
      };
    } else {
      favoritesData = { placeholder: true, showNoData: false, showLoading: false };
    }

    return (
      <>
        <title>Providers</title>
        <div className={classes.title}>
          <Typography className={classes.headerTitle}>Providers</Typography>

        </div>
        <ProvidersTabs tab='favorite_providers' />
        <div className={classes.container}>

          <DynamicTable
            header='Favorite Providers'
            showFilters={true}
            columns={this.columns}
            taxonomyCodes={taxonomyCodes}
            query={query}
            data={favoritesData}
            onQueryChange={this.onQueryChange}
            getRowHref={(row) => `/providers/${row.id}`}
          />
        </div>
      </>
    );
  }

  onQueryChange = (query) => {
    if (query.where && Object.keys(query.where).length === 0) {
      delete query.where;
    }
    this.setState({ query }, this.fetchData.bind(this));
  };
}

FavoriteProvidersPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  router: PropTypes.object.isRequired,
  clientId: PropTypes.number.isRequired,
  favorites: PropTypes.object.isRequired,
  userId: PropTypes.number.isRequired,
  taxonomyCodes: PropTypes.object,
};

function mapStateToProps(state) {
  const { favorites, taxonomyCodes } = state;
  const userId = state.user ? state.user.id : null;
  const clientId = state.app.selectedClient;

  return {
    userId,
    clientId,
    favorites: StoreUtil.get(favorites, 'by_type', `${clientId}-${userId}-Providers`),
    taxonomyCodes: StoreUtil.get(taxonomyCodes, StoreUtil.COMMON_TABLE),
  };
}

const styled = withStyles(styles)(FavoriteProvidersPage);
const connected = connect(mapStateToProps)(styled);
const routed = withRouter(connected);
export { routed as FavoriteProvidersPage };
